// frontend/src/modules/email/pages/LogTab.tsx
import * as React from 'react';
import { Mail, Search } from 'lucide-react';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import type { EmailLogPage, EmailLogEntry } from '../types';

const PAGE = 50;
type StatusFilter = 'all' | 'sent' | 'failed' | 'pending';

export function LogTab() {
  const [offset, setOffset] = React.useState(0);

  // Server-side filters: status + recipient substring are passed as query
  // params (?status= &q=) so they search the WHOLE log, not just the loaded
  // page. The recipient input is debounced to avoid a request per keystroke.
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [recipient, setRecipient] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(recipient.trim()), 300);
    return () => clearTimeout(t);
  }, [recipient]);

  // Back to the first page whenever a filter changes.
  React.useEffect(() => { setOffset(0); }, [status, debouncedQ]);

  const path = React.useMemo(() => {
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (status !== 'all') qs.set('status', status);
    if (debouncedQ) qs.set('q', debouncedQ);
    return `/api/email/log?${qs.toString()}`;
  }, [offset, status, debouncedQ]);

  const { data, loading } = useApi<EmailLogPage>(path);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const filtersActive = status !== 'all' || recipient.trim() !== '';

  return (
    <div className="max-w-6xl space-y-4">
      {/* Filters — applied server-side across the whole log */}
      <section className="flex items-center gap-3">
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="h-10 w-auto min-w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-xs">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="search"
            placeholder="Filter by recipient…"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
          />
        </div>
        {filtersActive && (
          <button
            type="button"
            onClick={() => { setStatus('all'); setRecipient(''); }}
            className="text-[12px] font-semibold text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
        {filtersActive && !loading && (
          <span className="text-[12px] text-muted-foreground ml-auto">
            {total} matching
          </span>
        )}
      </section>

      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<EmailLogEntry>
          loading={loading}
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            { key: 'when',    header: 'When',     cell: (r) => formatDateTime(r.created_at), className: 'text-muted-foreground' },
            { key: 'tpl',     header: 'Template', cell: (r) => <span className="font-mono text-[12px] uppercase tracking-wide text-foreground/85">{r.template_key}</span> },
            { key: 'to',      header: 'To',       cell: (r) => r.to_email },
            { key: 'subject', header: 'Subject',  cell: (r) => <span className="truncate inline-block max-w-md">{r.subject}</span> },
            { key: 'status',  header: 'Status',   cell: (r) => statusBadge(r) },
            { key: 'delivery', header: 'Delivery', cell: (r) => deliveryBadge(r) },
          ]}
          emptyState={
            <EmptyState
              icon={<Mail />}
              title={filtersActive ? 'No matching emails' : 'No emails yet'}
              description={filtersActive
                ? 'Nothing in the log matches those filters — try clearing them.'
                : 'Once you send invoices or hit Test send, entries show up here.'}
            />
          }
        />
      </section>
      {total > PAGE && (
        <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
          <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full h-9" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
            <Button size="sm" variant="outline" className="rounded-full h-9" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function statusBadge(r: EmailLogEntry): React.ReactNode {
  if (r.status === 'sent') return <span className={statusPill('success')}><span className={statusDot('success')} />Sent</span>;
  if (r.status === 'failed') return (
    <span className="flex items-center gap-2">
      <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Failed</span>
      {r.error_message && <span className="text-xs text-muted-foreground truncate max-w-xs">{r.error_message}</span>}
    </span>
  );
  return <span className={statusPill('info')}><span className={statusDot('info')} />Pending</span>;
}

/** Provider delivery state from the Resend webhook. Precedence: complained > bounced > delivered. */
function deliveryBadge(r: EmailLogEntry): React.ReactNode {
  if (r.complained_at) return <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Complained</span>;
  if (r.bounced_at)    return <span className={statusPill('warning')}><span className={statusDot('warning')} />Bounced</span>;
  if (r.delivered_at)  return <span className={statusPill('success')}><span className={statusDot('success')} />Delivered</span>;
  return <span className="text-muted-foreground">—</span>;
}
